import "./style.scss";
import { FC, useRef, useState } from "react";
import { useEffect } from "react";
import ArrowSvg from "@/src/svg/autoCompile/ArrowSvg";
import TrashSvg from "@/src/svg/admissionOfficePage/TrashSvg";
import EditorSvg from "@/src/svg/CustomUtils/EditorSvg";
import ArrowUpSvg from "@/src/svg/CustomUtils/ArrowUpSvg";
import CloseSvg from "@/src/svg/CustomUtils/CloseSvg";

function useClickOutside(ref: any, callback: () => void) {
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target)) {
        callback();
      }
    };

    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [ref, callback]);
}

interface Props {
  label?: string;
  value: string;
  onChange: (v: string) => void;
  onSelect: (v: string) => void;
  isOpen: boolean;
  options: string[];
  error?: boolean;
  required?: boolean;
  inputProps?: any;
  placeholder?: string;
  defaultValue?: string;
  identifier?: string;
  allowAdd?: boolean;
  onAddItem?: (value: string) => void;
}

const AutocompleteInput: FC<Props> = ({
  label,
  value,
  onChange,
  onSelect,
  isOpen,
  options,
  error,
  required,
  inputProps,
  placeholder,
  defaultValue,
  identifier,
  allowAdd = false,
  onAddItem,
}) => {
  const ref = useRef(null);
  const [isAddInputOpen, setIsAddInputOpen] = useState(false);
  const [addInputValue, setAddInputValue] = useState("");
  const showDropdown = isOpen && (options.length > 0 || allowAdd);

  useClickOutside(ref, () => {
    if (inputProps?.close) inputProps.close();
  });

  useEffect(() => {
    if (!value) {
      onChange(defaultValue || "");
      inputProps?.close?.();
    }
  }, []);

  // Keeps react-hook-form's internal state in sync with the controlled `value`.
  // Without this, typing or applying `defaultValue` updates only the local
  // autocomplete state, so RHF treats the field as empty and required-validation fails.
  useEffect(() => {
    const reg = inputProps?.register;
    if (reg?.onChange && reg?.name) {
      reg.onChange({
        target: { name: reg.name, value },
        type: "change",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleDelete = () => {
    onChange("");

    if (inputProps?.setValue && inputProps?.name) {
      inputProps.setValue(inputProps.name, "", {
        shouldDirty: true,
        shouldTouch: false,
        shouldValidate: false,
      });
    }

    if (inputProps?.clearErrors && inputProps?.name) {
      inputProps.clearErrors(inputProps.name);
    }

    inputProps?.close?.();
  };

  const handleAddButtonClick = () => {
    setIsAddInputOpen(true);
  };

  const handleAddInputClose = () => {
    setAddInputValue("");
    setIsAddInputOpen(false);
  };

  const handleAddInputSave = () => {
    const trimmed = addInputValue.trim();
    if (trimmed && allowAdd && onAddItem) {
      onAddItem(trimmed);
    }
    setAddInputValue("");
    setIsAddInputOpen(false);
  };

  return (
    <div className={`form-group-fields-input ${identifier ? identifier : ""}`} ref={ref}>
      {label && (
        <div className="form-group-fields-input-label">
          <label>
            {label}
            {required && <span>*</span>}
          </label>
          <div className="form-group-fields-input-label-line"></div>
        </div>
      )}
      <input
        {...inputProps.register}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => inputProps.setOpen?.(true)}
        onClick={() => inputProps.setOpen?.(true)}
        placeholder={placeholder}
        autoComplete="off"
      />
      {identifier === "offered-course-auto" || identifier === "certificate" ? (
        <div className="form-group-fields-input-svg">
          <div className={`form-group-fields-input-svg-arrow ${showDropdown ? "rotate" : ""}`}>
            <ArrowSvg />
          </div>
          <div className="form-group-fields-input-svg-line"></div>
          <div className="form-group-fields-input-svg-delete" onClick={handleDelete}>
            <TrashSvg />
          </div>
        </div>
      ) : (
        <div className={`form-group-fields-input-arrow ${showDropdown ? "rotate" : ""}`}>
          <ArrowSvg />
        </div>
      )}

      {error && <p className="autocomplete-field-error">This field is required</p>}

      {showDropdown && (
        <div className="autocomplete-dropdown">
          {options.length > 0 && (
            <ul className="autocomplete-list">
              {options.map((opt) => (
                <li key={opt} onClick={() => onSelect(opt)}>
                  {opt}
                </li>
              ))}
            </ul>
          )}
          {allowAdd && (
            <div className="autocomplete-add">
              {!isAddInputOpen && (
                <div className="autocomplete-add-button" onClick={handleAddButtonClick}>
                  <div className="autocomplete-add-button-text">
                    <p>Add option</p>
                  </div>
                  <div className="autocomplete-add-button-svg">
                    <EditorSvg />
                  </div>
                </div>
              )}
              {isAddInputOpen && (
                <div className="autocomplete-add-input">
                  <input
                    type="text"
                    value={addInputValue}
                    onChange={(e) => setAddInputValue(e.target.value)}
                    placeholder="Add option"
                  />
                  <div className="autocomplete-add-input-buttons">
                    <div className="autocomplete-add-input-buttons-save" onClick={handleAddInputSave}>
                      <ArrowUpSvg />
                    </div>
                    <div className="autocomplete-add-input-buttons-close" onClick={handleAddInputClose}>
                      <CloseSvg />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AutocompleteInput;
